/**
 * Custom Buy Buttons
 * Handles: quantity controls, notify stock, wishlist, compare
 * Listens to theme's variant-change and cart:update events
 */
(function () {
  'use strict';

  const WISHLIST_KEY = 'custom_wishlist';
  const COMPARE_KEY = 'custom_compare';

  /* ========== Utility ========== */
  function getStorage(key) {
    try {
      return JSON.parse(localStorage.getItem(key)) || [];
    } catch {
      return [];
    }
  }

  function setStorage(key, data) {
    localStorage.setItem(key, JSON.stringify(data));
  }

  function toggleInList(key, productId, productData) {
    const list = getStorage(key);
    const idx = list.findIndex((item) => String(item.id) === String(productId));
    if (idx > -1) {
      list.splice(idx, 1);
      setStorage(key, list);
      return false;
    }
    list.push({ id: productId, ...productData });
    setStorage(key, list);
    return true;
  }

  function isInList(key, productId) {
    return getStorage(key).some((item) => String(item.id) === String(productId));
  }

  /* ========== Quantity Controls ========== */
  function initQuantity(scope) {
    scope.querySelectorAll('.custom-quantity-wrapper').forEach((wrapper) => {
      const input = wrapper.querySelector('.custom-quantity-input');
      const minusBtn = wrapper.querySelector('.custom-quantity-btn--minus');
      const plusBtn = wrapper.querySelector('.custom-quantity-btn--plus');
      if (!input || !minusBtn || !plusBtn) return;

      const step = parseInt(input.step, 10) || 1;
      const min = parseInt(input.min, 10) || 1;

      minusBtn.addEventListener('click', () => {
        const current = parseInt(input.value, 10) || min;
        const newVal = Math.max(min, current - step);
        input.value = newVal;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });

      plusBtn.addEventListener('click', () => {
        const current = parseInt(input.value, 10) || min;
        const max = parseInt(input.max, 10) || 9999;
        const newVal = Math.min(max, current + step);
        input.value = newVal;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  /* ========== Variant Change Handler ========== */
  function handleVariantChange(event) {
    const { variant } = event.detail || {};
    if (!variant) return;

    const section = event.target.closest('[id^="shopify-section-"]');
    if (!section) return;

    // Update notify stock button visibility
    const notifyBtn = section.querySelector('[data-custom-notify-stock]');
    if (notifyBtn) {
      notifyBtn.hidden = variant.available;
      notifyBtn.dataset.variantId = variant.id;
    }

    // Update notify modal hidden input
    const notifyVariantInput = section.querySelector(
      '[data-custom-notify-modal] input[name="contact[variant_id]"]'
    );
    if (notifyVariantInput) {
      notifyVariantInput.value = variant.id;
    }
  }

  /* ========== Notify Stock ========== */
  function initNotifyStock(scope) {
    const btn = scope.querySelector('[data-custom-notify-stock]');
    const modal = scope.querySelector('[data-custom-notify-modal]');
    if (!btn || !modal) return;

    btn.addEventListener('click', () => modal.showModal());

    const closeBtn = modal.querySelector('[data-custom-notify-close]');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => modal.close());
    }

    modal.addEventListener('click', (e) => {
      if (e.target === modal) modal.close();
    });

    const form = modal.querySelector('[data-custom-notify-form]');
    const successEl = modal.querySelector('[data-custom-notify-success]');
    if (form && successEl) {
      form.addEventListener('submit', (e) => {
        e.preventDefault();
        const formData = new FormData(form);
        const email = formData.get('contact[email]');
        const variantId = formData.get('contact[variant_id]');
        const productTitle = formData.get('contact[product_title]');

        fetch(`${Shopify.routes.root}contact#contact_form`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            form_type: 'contact',
            utf8: '\u2713',
            'contact[email]': email,
            'contact[body]': `Notify me when ${productTitle} (variant ${variantId}) is back in stock.`,
            'contact[tags]': 'notify-stock',
          }),
        })
          .then(() => {
            form.hidden = true;
            successEl.hidden = false;
          })
          .catch(() => {
            form.hidden = true;
            successEl.hidden = false;
          });
      });
    }
  }

  /* ========== Wishlist ========== */
  function initWishlist(scope) {
    scope.querySelectorAll('[data-custom-wishlist]').forEach((btn) => {
      const productId = btn.dataset.productId;
      updateWishlistUI(btn, isInList(WISHLIST_KEY, productId));

      btn.addEventListener('click', () => {
        const productData = {
          handle: btn.dataset.productHandle,
          url: btn.dataset.productUrl,
          title: btn.dataset.productTitle,
          image: btn.dataset.productImage,
        };
        const added = toggleInList(WISHLIST_KEY, productId, productData);
        updateWishlistUI(btn, added);
      });
    });
  }

  function updateWishlistUI(btn, isActive) {
    btn.classList.toggle('is-active', isActive);
    const textEl = btn.querySelector('[data-wishlist-text]');
    if (textEl) {
      textEl.textContent = isActive
        ? (btn.dataset.removeText || 'Remove from wishlist')
        : (btn.dataset.addText || 'Add to wishlist');
    }
  }

  /* ========== Compare ========== */
  function initCompare(scope) {
    scope.querySelectorAll('[data-custom-compare]').forEach((btn) => {
      const productId = btn.dataset.productId;
      updateCompareUI(btn, isInList(COMPARE_KEY, productId));

      btn.addEventListener('click', () => {
        const productData = {
          handle: btn.dataset.productHandle,
          url: btn.dataset.productUrl,
          title: btn.dataset.productTitle,
          image: btn.dataset.productImage,
        };
        const added = toggleInList(COMPARE_KEY, productId, productData);
        updateCompareUI(btn, added);
      });
    });
  }

  function updateCompareUI(btn, isActive) {
    btn.classList.toggle('is-active', isActive);
    const textEl = btn.querySelector('[data-compare-text]');
    if (textEl) {
      textEl.textContent = isActive
        ? (btn.dataset.removeText || 'Remove from compare')
        : (btn.dataset.addText || 'Add to compare');
    }
  }

  /* ========== Init ========== */
  function initSection(section) {
    if (!section) return;

    const forms = section.querySelectorAll('form[id^="form"]');
    forms.forEach((form) => {
      form.addEventListener('variant-change', handleVariantChange);
    });

    initQuantity(section);
    initNotifyStock(section);
    initWishlist(section);
    initCompare(section);
  }

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('.custom-buy-buttons').forEach((el) => {
      const section = el.closest('[id^="shopify-section-"]');
      if (section) initSection(section);
    });
  });

  document.addEventListener('shopify:section:load', (event) => {
    initSection(event.target);
  });
})();
